import { DEFAULT_ALL_VALUE, getDurationStringSeconds, isDurationString } from '@chronosphereio/core';
import { partition, sortBy } from 'lodash';
import {
  DEFAULT_INTERVAL_VARIABLE_OPTIONS,
  DEFAULT_MIN_INTERVAL_SIZE,
  INTERVAL_VARIABLE_AUTO_VALUE,
  IntervalVariableDefinition,
  isListVariableDefinition,
  PanelDefinition,
  parseTemplateVariableNames,
  replaceTemplateVariables,
  VariableDefinition,
  VariableOption,
  VariableState,
  VariableStateMap,
  VariableValue,
} from '@/core';
/*
 * Check whether saved variable definitions are out of date with current default list values in Zustand store
 */
export function checkSavedDefaultVariableStatus(definitions: VariableDefinition[], varState: VariableStateMap) {
  let isSavedVariableModified = false;
  const modifiedVariableNames: string[] = [];
  for (const savedVariable of definitions) {
    const currentVariable = varState[savedVariable.spec.name];
    if (!currentVariable || currentVariable.value == null) continue;
    if (savedVariable.kind === 'ListVariable') {
      // Account for when multi select is enabled (allow_multiple: true) but only one variable value is selected
      const isSingleValueSelected = Array.isArray(currentVariable.value) && currentVariable.value.length === 1;
      let isSingleValueModified =
        isSingleValueSelected && currentVariable.value[0] !== savedVariable.spec.default_value;

      // Ignore modified status when saving only changes: ['example'] -> 'example'
      if (Array.isArray(savedVariable.spec.default_value) && savedVariable.spec.default_value.length === 1) {
        if (currentVariable.value[0] === savedVariable.spec.default_value[0]) {
          isSingleValueModified = false;
        }
      }

      if (
        isSingleValueModified ||
        (!isSingleValueSelected && currentVariable.value !== savedVariable.spec.default_value)
      ) {
        modifiedVariableNames.push(savedVariable.spec.name);
        isSavedVariableModified = true;
      }
    } else if (savedVariable.kind === 'TextVariable') {
      const currentVariableValue = typeof currentVariable.value === 'string' ? currentVariable.value : '';
      if (savedVariable.spec.value !== currentVariableValue) {
        modifiedVariableNames.push(savedVariable.spec.name);
        isSavedVariableModified = true;
      }
    } else if (savedVariable.kind === 'IntervalVariable') {
      if (currentVariable.value !== savedVariable.spec.default_value) {
        modifiedVariableNames.push(savedVariable.spec.name);
        isSavedVariableModified = true;
      }
    }
  }
  return { isSavedVariableModified, modifiedVariableNames };
}

/**
 * If the All value is selected, ensure that no other values are selected.
 * The All value should be in the last position of the array if it was recently selected.
 */
export function ensureOnlyAllValueIsSelected(values: string[]) {
  // Make sure there is only one all value
  let nextValues = values;
  if (Array.isArray(values) && values.includes(DEFAULT_ALL_VALUE)) {
    if (values.at(-1) === DEFAULT_ALL_VALUE) {
      nextValues = [DEFAULT_ALL_VALUE];
    } else {
      nextValues = nextValues.filter((v) => v !== DEFAULT_ALL_VALUE);
    }
  }
  return nextValues;
}

/**
 * Check if all value is selected. This can be a string or an array of strings
 */
export function isAllValueSelected(value: string[] | string | null | undefined) {
  return Array.isArray(value) ? value.includes(DEFAULT_ALL_VALUE) : value === DEFAULT_ALL_VALUE;
}

function hydrateInitialVariableState(variable: VariableDefinition, initialValue?: VariableValue): VariableState {
  const varState: VariableState = {
    value: null,
    loading: false,
    hasLoaded: false,
  };
  switch (variable.kind) {
    case 'TextVariable':
      varState.value = initialValue ?? variable.spec.value;
      break;
    case 'ListVariable':
      varState.options = [];
      varState.value = initialValue ?? variable.spec.default_value ?? null;
      // TODO: smarter fallbacks for default_value when allow_all_value is true
      if (varState.options.length > 0 && !varState.value) {
        const firstOptionValue = varState.options[0]?.value ?? null;
        if (firstOptionValue !== null) {
          varState.value = variable.spec.allow_multiple ? [firstOptionValue] : firstOptionValue;
        }
      }

      // Ensure that the value is an array if the variable allows multiple values
      if (typeof varState.value === 'string' && variable.spec.allow_multiple) {
        varState.value = [varState.value];
      }

      // "all" variable handling assumes the value is not in an array. This is
      // handled properly during internal variable interactions, but it is possible
      // to end up in a buggy state if the variables are initialized with an "all"
      // value inside an array. When hydrating variables, normalize this to minimize
      // bugs.
      if (Array.isArray(varState.value) && varState.value.length === 1 && varState.value[0] === DEFAULT_ALL_VALUE) {
        varState.value = DEFAULT_ALL_VALUE;
      }

      break;
    case 'IntervalVariable':
      // Disabling to avoid refactoring when enabling eslint:recommended rules.
      // eslint-disable-next-line no-case-declarations
      const options = getIntervalVariableOptions(variable as IntervalVariableDefinition);
      varState.options = options.map((v) => ({
        label: v === INTERVAL_VARIABLE_AUTO_VALUE ? 'Auto' : v,
        value: v,
      }));
      varState.value = initialValue ?? variable.spec.default_value ?? options[0] ?? DEFAULT_MIN_INTERVAL_SIZE;
      break;
    default:
      break;
  }
  return varState;
}

export function hydrateInitialVariableStates(
  definitions: VariableDefinition[],
  initialValues: Record<string, VariableValue>
): VariableStateMap {
  const state: VariableStateMap = {};
  definitions.forEach((v) => {
    const name = v.spec.name;
    const param = initialValues[name];
    const initialValue = param ? param : null;
    state[name] = hydrateInitialVariableState(v, initialValue);
  });

  return state;
}

/**
 * Resolves the runtime default value for a ListVariable when the hydrated
 * default is null (no explicit `default_value` in the spec). Mirrors the
 * fallback logic in `setVariableOptions` so that `defaultView` reflects
 * the value the user actually sees after options load.
 */
export function resolveRuntimeDefault(
  variableDef: VariableDefinition,
  options: VariableOption[]
): VariableValue | null {
  if (!isListVariableDefinition(variableDef) || options.length === 0) {
    return null;
  }

  if (variableDef.spec.default_value) {
    const defaultValue = variableDef.spec.default_value;
    const defaultInOptions = Array.isArray(defaultValue)
      ? defaultValue.every((value) => options.find((option) => option.value === value))
      : options.find((option) => option.value === defaultValue);
    if (defaultInOptions) {
      return defaultValue;
    }
  }

  if (variableDef.spec.allow_all_value) {
    return DEFAULT_ALL_VALUE;
  }

  const firstOption = options[0];
  if (firstOption) {
    return variableDef.spec.allow_multiple ? [firstOption.value] : firstOption.value;
  }

  return null;
}

/**
 * When allow_all_value is set, ensure correct options are defined.
 * Accounts for custom_all_value, 'All' selection state.
 */
export function processVariableAllState(
  definitions: VariableDefinition[],
  originalValues: VariableStateMap
): VariableStateMap {
  const contextValues: VariableStateMap = {};

  // This will loop through all the current variables values
  // and update any variables that have ALL_VALUE as their current value
  // to include all options.
  Object.keys(originalValues).forEach((name) => {
    const v = { ...originalValues[name] } as VariableState;

    if (isAllValueSelected(v.value)) {
      const definition = definitions.find((d) => d.spec.name === name);
      // If the variable is a list variable and has a custom all value, then use that value instead
      if (definition?.kind === 'ListVariable' && definition.spec.custom_all_value) {
        v.value = definition.spec.custom_all_value;
      } else {
        v.value = v.options?.map((o: { value: string }) => o.value) ?? null;
      }
    }
    contextValues[name] = v;
  });

  // Replace any string value that includes a template variable with the value of that variable
  // This is needed when the Custom All Value has a variable in it
  Object.entries(contextValues).forEach(([_, variable]) => {
    const value = variable.value;
    if (typeof value === 'string') {
      variable.value = replaceTemplateVariables(value, contextValues);
    }
  });

  return contextValues;
}

/**
 * Sorts interval options by duration, shortest first.
 * The "Auto" option is always placed first when present.
 * Invalid durations are placed at the end.
 */
function sortIntervalOptions(options: string[]): string[] {
  const autoOption = options.filter((option) => option === INTERVAL_VARIABLE_AUTO_VALUE);
  const durationOptions = options.filter((option) => option !== INTERVAL_VARIABLE_AUTO_VALUE);
  // Partition into valid and invalid durations, sort valid ones, append invalid at end
  const [valid, invalid] = partition(durationOptions, isDurationString);
  const sorted = sortBy(valid, (duration) => getDurationStringSeconds(duration));
  return [...autoOption, ...sorted, ...invalid];
}

export function getIntervalVariableOptions(definition: IntervalVariableDefinition): string[] {
  const options: string[] = [];
  if (definition.spec.auto === true) {
    options.push(INTERVAL_VARIABLE_AUTO_VALUE);
  }
  const allOptions = options.concat(definition.spec.options ?? DEFAULT_INTERVAL_VARIABLE_OPTIONS);
  return sortIntervalOptions(allOptions);
}

/**
 * Detects if a query string contains a reference to a variable.
 * @param query The query string to search
 * @param variableName The variable name to look for (without the $ prefix)
 * @returns true if the variable is found in the query
 */
export function detectVariableUsage(query: string, variableName: string): boolean {
  if (!query || !variableName) {
    return false;
  }
  const foundVariables = parseTemplateVariableNames(query);
  return foundVariables.includes(variableName);
}

/**
 * Scans panel definitions to determine which variables each panel uses.
 */
export function scanPanelsForVariableUsage(
  panels: Record<string, PanelDefinition>,
  variableNames: string[]
): Record<string, string[]> {
  const usageMap: Record<string, string[]> = {};

  if (variableNames.length === 0) {
    return usageMap;
  }

  const variableNamesSet = new Set(variableNames);

  Object.entries(panels).forEach(([panelKey, panel]) => {
    const usedVariables = new Set<string>();

    // Check all queries in the panel
    panel.spec.queries?.forEach((queryDef) => {
      const query = queryDef.spec.plugin.spec?.query;
      if (typeof query === 'string') {
        const foundVariables = parseTemplateVariableNames(query);
        foundVariables.forEach((v) => {
          if (variableNamesSet.has(v)) {
            usedVariables.add(v);
          }
        });
      }
    });

    // Also check panel name for variable usage
    const panelName = panel.spec.display?.name;
    if (typeof panelName === 'string') {
      const foundVariables = parseTemplateVariableNames(panelName);
      foundVariables.forEach((v) => {
        if (variableNamesSet.has(v)) {
          usedVariables.add(v);
        }
      });
    }

    usageMap[panelKey] = Array.from(usedVariables);
  });

  return usageMap;
}

/**
 * Validates a label name. Must be non-empty, start with a letter or underscore,
 * and contain only letters, digits, or underscores.
 */
export function isValidLabelName(name: string): boolean {
  if (!name || name.trim() === '') {
    return false;
  }
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
}
